import React, { useEffect, useState, useContext } from 'react'
import { Button } from "@/components/ui/button"
import { getRoleById } from '@/services/roleService'
import { FolderOpen, AlertTriangle } from 'lucide-react'
import { ProjectContext } from '@/components/create-project/context/ProjectContext'

interface Project {
  projectId: string;
  name: string;
}

export default function ViewRole() {
    const [roleData, setRoleData] = useState<any>(null);
    const projectContext = useContext(ProjectContext);
    const [projects, setProjects] = useState<Project[]>([]);

    const searchParams = new URLSearchParams(window.location.search);
    const roleId = searchParams.get('id') ?? '';
    
    useEffect(() => {
        getRoleById(roleId).then((data: any) => setRoleData(data.data));
    }, [roleId]);

    useEffect(() => {
        if (projectContext?.projects) {
            setProjects(projectContext.projects);
        }
    }, [projectContext?.projects]);

    // Get project names for the role's assigned projects
    const getProjectName = (projectId: string) => {
        const project = projects.find(p => p.projectId === projectId);
        return project?.name || projectId;
    };

  return (
    <div className="pt-20 sm:pt-10 px-0 2xl:px-44">
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="text-gray-600 hover:text-gray-900"
        >
          ← Back
        </Button>
        <h1 className="text-lg sm:text-xl font-semibold">View Role</h1>
      </div>

      <div className="bg-white rounded-xl p-4 sm:p-6">
        <div className="py-4">
          <div className="pb-6 border-b border-gray-100">
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className='col-span-1'>
                <label className="text-sm text-gray-500 block">Role Name</label>
                <div className="mt-1 w-full p-2 bg-gray-50 rounded-md border border-gray-200">
                  {roleData?.name}
                </div>
              </div>
              <div className='col-span-1'>
                <label className="text-sm text-gray-500 block">Description</label>
                <div className="mt-1 w-full p-2 bg-gray-50 rounded-md border border-gray-200">
                  {roleData?.description || '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Project Access Section */}
          <div className="py-6 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <FolderOpen className="w-5 h-5 text-gray-600" />
              <h2 className="text-base font-medium text-gray-900">Project Access</h2>
            </div>
            
            {roleData?.type === 'System-Defined' ? (
              <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
                System-defined roles have account-wide permissions and do not require project assignment.
              </div>
            ) : (
              <>
                {(!roleData?.projects || roleData.projects.length === 0) ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      This role has no project access. Team members with this role cannot access any project resources.
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {roleData.projects.map((projectId: string) => (
                      <div 
                        key={projectId} 
                        className="px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full text-sm text-primary-700"
                      >
                        {getProjectName(projectId)}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="py-6 space-y-6">
            {roleData?.categories?.map((category: any) => (
              <div key={category.id} className="border border-primary-100 rounded-xl overflow-hidden">
                <div className="bg-primary-50 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-900">
                    {category.name}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 p-4">
                  {category.permissions.map((permission: any) => (
                    <div key={permission.id} className="py-2 flex items-center gap-2">
                      <div className={`w-4 h-4 rounded flex items-center justify-center ${permission.selected ? 'bg-primary-500' : 'bg-gray-200'}`}>
                        {permission.selected && <span className="text-white text-xs font-semibold">✓</span>}
                      </div>
                      <label className="text-sm text-gray-500">
                        {permission.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
